import React, { useState } from 'react';
import { Plus, Search, Mail, Phone, Calendar, Briefcase, Edit, Trash2, Users, Camera, Upload } from 'lucide-react';

const EmployeeList = ({ employees, setEmployees, onEmployeeDelete }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    email: '',
    phone: '',
    photo: '',
    department: '',
    joinDate: ''
  });
  const [dragActive, setDragActive] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  const filteredEmployees = employees.filter(employee =>
    employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.department.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      email: '',
      phone: '',
      photo: '',
      department: '',
      joinDate: ''
    });
    setEditingEmployee(null);
    setShowAddForm(false);
    setPhotoPreview(null);
    setDragActive(false);
  };

  const handlePhotoUpload = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const photoData = e.target.result;
        setPhotoPreview(photoData);
        setFormData({...formData, photo: photoData});
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handlePhotoUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handlePhotoUpload(e.target.files[0]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingEmployee) {
      setEmployees(employees.map(emp => 
        emp.id === editingEmployee.id 
          ? { ...formData, id: editingEmployee.id }
          : emp
      ));
    } else {
      const newEmployee = {
        ...formData,
        id: Date.now().toString(),
        joinDate: formData.joinDate || new Date().toISOString().split('T')[0]
      };
      setEmployees([...employees, newEmployee]);
    }
    
    resetForm();
  };

  const handleEdit = (employee) => {
    setFormData(employee);
    setEditingEmployee(employee);
    setShowAddForm(true);
    setPhotoPreview(employee.photo || null);
  };

  const handleDelete = (employeeId) => {
    if (window.confirm('Are you sure you want to delete this employee? This will also delete all their attendance records and tasks permanently.')) {
      // Use cascading delete function from App.js
      onEmployeeDelete(employeeId);
    }
  };

  const renderEmployeeCard = (employee, index) => (
    <div 
      key={employee.id}
      className="group relative bg-gradient-to-br from-white/90 via-white/80 to-white/70 backdrop-blur-xl rounded-3xl shadow-xl hover:shadow-2xl p-6 transition-all duration-500 transform hover:scale-[1.02] hover:-translate-y-3 animate-slide-up border border-white/30 overflow-hidden"
      style={{ animationDelay: `${index * 150}ms` }}
    >
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/20 via-secondary-50/20 to-purple-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>
      
      {/* Floating particles effect */}
      <div className="absolute top-4 right-4 w-2 h-2 bg-primary-400 rounded-full animate-bounce opacity-60" style={{ animationDelay: '0.5s' }}></div>
      <div className="absolute top-8 right-8 w-1 h-1 bg-secondary-400 rounded-full animate-bounce opacity-40" style={{ animationDelay: '1s' }}></div>
      <div className="absolute bottom-8 left-8 w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce opacity-50" style={{ animationDelay: '1.5s' }}></div>
      
      <div className="relative z-10">
        {/* Action buttons - positioned at top right */}
        <div className="absolute top-0 right-0 flex flex-col lg:flex-col space-y-2 lg:space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2 lg:space-x-0 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-x-4 group-hover:translate-x-0 z-20">
          <button
            onClick={() => handleEdit(employee)}
            className="p-3 text-primary-600 hover:text-white bg-primary-50 hover:bg-gradient-to-r hover:from-primary-500 hover:to-primary-600 rounded-2xl transition-all duration-300 transform hover:scale-110 hover:rotate-3 shadow-lg hover:shadow-xl"
          >
            <Edit className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleDelete(employee.id)}
            className="p-3 text-red-600 hover:text-white bg-red-50 hover:bg-gradient-to-r hover:from-red-500 hover:to-red-600 rounded-2xl transition-all duration-300 transform hover:scale-110 hover:-rotate-3 shadow-lg hover:shadow-xl"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>

        {/* Profile section - centered */}
        <div className="flex flex-col items-center text-center mb-6">
          <div className="relative group/avatar mb-4">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-3xl blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
            <img
              src={employee.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(employee.name)}&background=random&size=100`}
              alt={employee.name}
              className="relative w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-3xl object-cover ring-4 ring-white shadow-2xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 mx-auto"
            />
            <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full border-3 border-white animate-pulse shadow-lg">
              <div className="w-full h-full bg-green-300 rounded-full animate-ping"></div>
            </div>
          </div>
          
          {/* Name - centered under profile pic */}
          <div className="group/name mb-4 w-full">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 bg-clip-text text-transparent leading-tight group-hover:from-primary-600 group-hover:via-secondary-600 group-hover:to-purple-600 transition-all duration-500 text-center px-2">
              {employee.name}
            </h3>
          </div>
          
          {/* Role and Department - centered */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full">
            <div className="animate-slide-up" style={{ animationDelay: `${index * 150 + 200}ms` }}>
              <p className="inline-flex items-center px-3 py-1.5 text-xs sm:text-sm font-bold bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full shadow-lg transform hover:scale-105 transition-all duration-300 animate-pulse">
                <Briefcase className="w-3 h-3 mr-1.5" />
                {employee.role}
              </p>
            </div>
            <div className="animate-slide-up" style={{ animationDelay: `${index * 150 + 300}ms` }}>
              <p className="inline-flex items-center px-3 py-1.5 text-xs sm:text-sm font-semibold bg-gradient-to-r from-secondary-500 to-purple-500 text-white rounded-full shadow-lg transform hover:scale-105 transition-all duration-300 animate-bounce">
                <Users className="w-3 h-3 mr-1.5" />
                {employee.department}
              </p>
            </div>
          </div>
        </div>
        
        <div className="space-y-4 mt-8">
          <div className="group/item flex items-center space-x-4 p-4 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-2xl hover:from-blue-100/90 hover:to-cyan-100/90 transition-all duration-300 transform hover:scale-[1.02] animate-slide-up border border-blue-100/50" style={{ animationDelay: `${index * 150 + 400}ms` }}>
            <div className="p-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl shadow-lg group-hover/item:scale-110 group-hover/item:rotate-6 transition-all duration-300">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">Email</p>
              <p className="text-sm sm:text-base font-medium text-gray-800 break-all leading-relaxed">{employee.email}</p>
            </div>
          </div>
          
          <div className="group/item flex items-center space-x-4 p-4 bg-gradient-to-r from-green-50/80 to-emerald-50/80 rounded-2xl hover:from-green-100/90 hover:to-emerald-100/90 transition-all duration-300 transform hover:scale-[1.02] animate-slide-up border border-green-100/50" style={{ animationDelay: `${index * 150 + 500}ms` }}>
            <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl shadow-lg group-hover/item:scale-110 group-hover/item:rotate-6 transition-all duration-300">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-green-600 font-semibold uppercase tracking-wide">Phone</p>
              <p className="text-sm sm:text-base font-medium text-gray-800 break-all leading-relaxed">{employee.phone}</p>
            </div>
          </div>
          
          <div className="group/item flex items-center space-x-4 p-4 bg-gradient-to-r from-purple-50/80 to-pink-50/80 rounded-2xl hover:from-purple-100/90 hover:to-pink-100/90 transition-all duration-300 transform hover:scale-[1.02] animate-slide-up border border-purple-100/50" style={{ animationDelay: `${index * 150 + 600}ms` }}>
            <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl shadow-lg group-hover/item:scale-110 group-hover/item:rotate-6 transition-all duration-300">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-purple-600 font-semibold uppercase tracking-wide">Joined</p>
              <p className="text-sm sm:text-base font-medium text-gray-800 leading-relaxed">
                {new Date(employee.joinDate).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEmployeeForm = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-40 animate-fade-in overflow-y-auto pt-20 lg:pt-4">
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-sm sm:max-w-md lg:max-w-lg animate-scale-in border border-white/20 my-4 sm:my-8 max-h-[calc(95vh-5rem)] lg:max-h-[95vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent text-center">
            {editingEmployee ? '✏️ Edit Employee' : '➕ Add New Employee'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-5">
          {/* Photo Upload Section */}
          <div className="flex justify-center mb-6 animate-slide-up" style={{ animationDelay: '50ms' }}>
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="photo-upload"
              />
              <label
                htmlFor="photo-upload"
                className={`relative flex flex-col items-center justify-center w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full border-4 border-dashed cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                  dragActive 
                    ? 'border-primary-500 bg-primary-50 scale-105' 
                    : 'border-gray-300 hover:border-primary-400 bg-gray-50 hover:bg-primary-50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {photoPreview ? (
                  <>
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="w-full h-full rounded-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 rounded-full opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <Camera className="w-6 h-6 text-white" />
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center text-gray-500 hover:text-primary-600 transition-colors duration-300">
                    <Upload className="w-6 h-6 sm:w-8 sm:h-8 mb-1" />
                    <span className="text-xs sm:text-sm font-medium text-center px-2">
                      {dragActive ? 'Drop here' : 'Upload Photo'}
                    </span>
                  </div>
                )}
              </label>
              {photoPreview && (
                <button
                  type="button"
                  onClick={() => {
                    setPhotoPreview(null);
                    setFormData({...formData, photo: ''});
                  }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors duration-200 text-xs"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 bg-gray-50 hover:bg-white"
              placeholder="Enter employee name"
            />
          </div>
          
          <div className="animate-slide-up" style={{ animationDelay: '150ms' }}>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Job Role</label>
            <input
              type="text"
              required
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 bg-gray-50 hover:bg-white"
              placeholder="e.g. Frontend Developer"
            />
          </div>
          
          <div className="animate-slide-up" style={{ animationDelay: '200ms' }}>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 bg-gray-50 hover:bg-white"
              placeholder="employee@company.com"
            />
          </div>
          
          <div className="animate-slide-up" style={{ animationDelay: '250ms' }}>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 bg-gray-50 hover:bg-white"
              placeholder="+1-234-567-8900"
            />
          </div>
          
          <div className="animate-slide-up" style={{ animationDelay: '300ms' }}>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Department</label>
            <input
              type="text"
              required
              value={formData.department}
              onChange={(e) => setFormData({...formData, department: e.target.value})}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 bg-gray-50 hover:bg-white"
              placeholder="e.g. Engineering"
            />
          </div>
          
          <div className="animate-slide-up" style={{ animationDelay: '350ms' }}>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Join Date</label>
            <input
              type="date"
              value={formData.joinDate}
              onChange={(e) => setFormData({...formData, joinDate: e.target.value})}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 bg-gray-50 hover:bg-white"
            />
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-4 sm:pt-6 animate-slide-up" style={{ animationDelay: '450ms' }}>
            <button
              type="submit"
              className="flex-1 bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 font-bold text-sm sm:text-lg"
            >
              {editingEmployee ? '✅ Update Employee' : '🚀 Add Employee'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="flex-1 bg-gray-200 text-gray-700 px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl hover:bg-gray-300 transition-all duration-300 font-bold text-sm sm:text-lg hover:scale-105 transform"
            >
              ❌ Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 animate-slide-down">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
            👥 Team Members
          </h2>
          <p className="text-gray-600 mt-2 flex items-center space-x-2">
            <Users className="w-5 h-5 text-primary-500" />
            <span>{employees.length} total employees</span>
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="group bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-3 rounded-2xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 flex items-center space-x-3 shadow-lg"
        >
          <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
          <span className="font-semibold">Add Employee</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative animate-slide-up" style={{ animationDelay: '100ms' }}>
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <Search className="w-5 h-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="🔍 Search employees by name, role, or department..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-12 pr-4 py-4 bg-white/70 backdrop-blur-sm border border-white/20 rounded-2xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 text-gray-700 placeholder-gray-500 shadow-lg"
        />
      </div>

      {/* Employee Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
        {filteredEmployees.map((employee, index) => 
          renderEmployeeCard(employee, index)
        )}
      </div>

      {filteredEmployees.length === 0 && (
        <div className="text-center py-16 animate-slide-up">
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-12 shadow-lg border border-white/20 max-w-md mx-auto">
            <Users className="w-20 h-20 text-gray-300 mx-auto mb-6" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No employees found</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm 
                ? "Try adjusting your search terms or add a new employee" 
                : "Get started by adding your first team member"
              }
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-3 rounded-2xl hover:shadow-lg transition-all duration-300 transform hover:scale-105"
            >
              Add First Employee
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showAddForm && renderEmployeeForm()}
    </div>
  );
};

export default EmployeeList;